import React, { createContext, useContext, useMemo, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useWebSocket as usePartyWebSocket } from 'partysocket/react';
import type ReconnectingWebSocket from 'partysocket/ws';

import { getEnvRequired } from '../utilities/env';
import { getCookieFromName } from '../utilities/cookies';
import { attachDossierSocketListeners } from '../real-time/dossierSocketListeners';
import { datadogLogger } from '../datadog/logger';

import { useAuth } from './AuthProvider';

type WebSocketContextValue = { socket: ReconnectingWebSocket | null };
const WebSocketContext = createContext<WebSocketContextValue>({ socket: null });

const authenticationEvent = () =>
    JSON.stringify({ type: 'authenticate', payload: getCookieFromName('token') });

export const WebSocketProvider = ({ children }: { children?: React.ReactNode }) => {
    const dispatch = useDispatch();
    const { isAuthenticated } = useAuth();

    const socketRef = useRef<ReconnectingWebSocket | null>(null);
    const sessionStartTimeRef = useRef<number | null>(null);
    const disconnectionStartTimeRef = useRef<number | null>(null);

    // Gate connection by auth + verify we are in a browser environment
    const url =
        isAuthenticated && typeof window !== 'undefined'
            ? getEnvRequired('SOCKET_SERVER_URL')
            : null;

    const handleOpen = useCallback((event: Event) => {
        // fallback to ref only if websocket unavailable on open event
        const ws = (event.target as ReconnectingWebSocket) ?? socketRef.current;
        if (ws) socketRef.current = ws;

        const retryCount = ws?.retryCount ?? 0;
        const now = Date.now();

        const downMs = disconnectionStartTimeRef.current
            ? now - disconnectionStartTimeRef.current
            : null;

        if (retryCount === 0) {
            sessionStartTimeRef.current = now;
        } else {
            datadogLogger.info('WebSocket reconnected successfully', {
                event: 'websocket_reconnected',
                retry_count: retryCount,
                connection_type: 'reconnection',
                disconnection_duration_ms: downMs,
                disconnection_duration_seconds: downMs ? Math.round(downMs / 1000) : null,
                timestamp: now,
            });
        }

        disconnectionStartTimeRef.current = null;
        ws?.send(authenticationEvent());
    }, []);

    const handleClose = useCallback(
        (event: CloseEvent) => {
            const ws = (event.target as ReconnectingWebSocket) ?? socketRef.current;
            if (ws) socketRef.current = ws;

            const retryCount = ws?.retryCount ?? 0;
            const now = Date.now();

            disconnectionStartTimeRef.current = now;

            const sessionMs = sessionStartTimeRef.current
                ? now - sessionStartTimeRef.current
                : null;

            // Downgrade normal/intentional closes
            const isNormalClose =
                // 1000 = normal close
                event.code === 1000 ||
                // 1001 = going away (page unload/navigation)
                event.code === 1001 ||
                // library/browser judged clean
                event.wasClean === true ||
                // intentional (logged out)
                !isAuthenticated ||
                // intentional (no URL)
                url == null;

            if (isNormalClose) {
                // Do nothing — don't log normal closes at all
                return;
            }

            datadogLogger.warn('WebSocket closed', {
                event: 'websocket_disconnected',
                retry_count: retryCount,
                close_code: event.code,
                close_reason: event.reason || 'unknown',
                was_clean: event.wasClean,
                session_duration_ms: sessionMs,
                session_duration_minutes: sessionMs ? Math.round(sessionMs / 60000) : null,
                timestamp: now,
            });
        },
        [isAuthenticated, url],
    );

    const handleError = useCallback((event: Event) => {
        const ws = (event.target as ReconnectingWebSocket) ?? socketRef.current;
        if (ws) socketRef.current = ws;

        const retryCount = ws?.retryCount ?? 0;

        datadogLogger.error('WebSocket error occurred', {
            event: 'websocket_error',
            retry_count: retryCount,
            error_type: event.type || 'unknown',
        });
    }, []);

    const socket = usePartyWebSocket(url ?? '', [], {
        connectionTimeout: 10000,
        debug: false,
        onOpen: handleOpen,
        onClose: handleClose,
        onError: handleError,
    });

    useEffect(() => {
        socketRef.current = socket;
    }, [socket]);

    useEffect(() => {
        if (!socket || !isAuthenticated) return;
        const cleanup = attachDossierSocketListeners(dispatch, socket);
        return () => cleanup?.();
    }, [socket, dispatch, isAuthenticated]);

    const contextValue = useMemo(() => ({ socket }), [socket]);
    return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
};

export const useWebSocket = () => useContext(WebSocketContext);
